import { apiRequest } from "@kira/api-client";
import { ImageProductSearch } from "./ImageProductSearch";
import { SellerOverview, CarpoolManagement, DemandWall, SellerWallet, MarketSurveys, AfterSaleGroups } from "./MarketOperations";
import { WishPool } from "./MeetingFeatures";
import type { Artist, CartItem, Order, Product, User } from "@kira/contracts";
import {
  ArrowLeft,
  ArrowUpLeft,
  Plus,
  Tag,
  BadgeCheck,
  Banknote,
  BellRing,
  Check,
  ChevronRight,
  CircleDollarSign,
  ClipboardList,
  Clock3,
  CreditCard,
  Heart,
  MessageCircleMore,
  PackageCheck,
  PenLine,
  Search,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Star,
  Store,
  Truck,
  UsersRound,
  WalletCards,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import "./market-v2.css";

export type MarketV2Mode = "buyer" | "seller";
export type MarketV2Panel =
  | "home"
  | "wish"
  | "cart"
  | "carpool"
  | "buyer-orders"
  | "follows"
  | "publish"
  | "start-carpool"
  | "demands"
  | "seller-orders"
  | "wallet"
  | "surveys"
  | "after-sale"
  | "seller-profile";

export interface MarketV2Review {
  id: string;
  sellerId: string;
  orderId?: string;
  authorName: string;
  authorAvatarUrl?: string;
  rating: 1 | 2 | 3 | 4 | 5;
  content: string;
  tags?: string[];
  images?: string[];
  sellerReply?: string;
  createdAt: string;
}

export interface MarketV2SellerProfile {
  id: string;
  displayName: string;
  avatarUrl?: string;
  bio?: string;
  verified?: boolean;
  rating?: number;
  positiveRate?: number;
  completedOrders?: number;
  followerCount?: number;
  isFollowed?: boolean;
  reviewTags?: Array<{ label: string; count: number }>;
  reviews?: MarketV2Review[];
}

export interface MarketV2OrderView {
  order: Order;
  deliveryAddress?: string;
  refundReason?: string;
  refundRequestedAt?: string;
  reviewStatus?: "pending" | "submitted";
  trackingEvents?: Array<{ id: string; content: string; occurredAt: string }>;
}

export interface MarketV2DemandWish {
  id: string;
  authorNameMasked: string;
  budgetCents: number;
  quantity: number;
  note?: string;
  createdAt: string;
  status: "open" | "followed" | "fulfilled";
}

export interface MarketV2DemandGroup {
  id: string;
  title: string;
  artistId?: string;
  artistName: string;
  coverUrl?: string;
  wishCount: number;
  medianBudgetCents: number;
  activeSellerCount: number;
  isFollowed?: boolean;
  wishes: MarketV2DemandWish[];
}

export interface MarketV2BankCard {
  id: string;
  bankName: string;
  lastFour: string;
  isDefault?: boolean;
}

export interface MarketV2Wallet {
  availableCents: number;
  pendingCents: number;
  frozenCents: number;
  bankCards: MarketV2BankCard[];
  recentEntries?: Array<{
    id: string;
    title: string;
    amountCents: number;
    direction: "income" | "expense";
    createdAt: string;
  }>;
}

export interface MarketV2Survey {
  id: string;
  title: string;
  description?: string;
  status: "draft" | "active" | "closed";
  responseCount: number;
  updatedAt: string;
}

export interface MarketV2AfterSaleGroup {
  id: string;
  name: string;
  relatedTitle: string;
  memberCount: number;
  notice?: string;
  isOwner?: boolean;
  muted?: boolean;
  messages?: Array<{ id: string; authorName: string; content: string; createdAt: string }>;
}

export interface MarketV2Actions {
  onBack?: () => void;
  onOpenProduct?: (product: Product) => unknown | Promise<unknown>;
  onAddToCart?: (product: Product) => unknown | Promise<unknown>;
  onJoinCarpool?: (product: Product) => unknown | Promise<unknown>;
  onToggleProductFollow?: (product: Product, followed: boolean) => unknown | Promise<unknown>;
  onToggleSellerFollow?: (seller: MarketV2SellerProfile, followed: boolean) => unknown | Promise<unknown>;
  onToggleCartItem?: (productId: string, selected: boolean) => unknown | Promise<unknown>;
  onSelectSellerCart?: (sellerId: string, selected: boolean) => unknown | Promise<unknown>;
  onChangeCartQuantity?: (productId: string, quantity: number) => unknown | Promise<unknown>;
  onCheckout?: (items: CartItem[]) => unknown | Promise<unknown>;
  onStartProductPublish?: (type: Product["type"]) => unknown | Promise<unknown>;
  onStartCarpoolPublish?: (product?: Product) => unknown | Promise<unknown>;
  onSellerOrderAction?: (
    order: Order,
    action: "ship" | "view-logistics" | "handle-refund" | "contact-buyer",
  ) => unknown | Promise<unknown>;
  onBuyerOrderAction?: (
    order: Order,
    action: "pay" | "confirm" | "refund" | "review" | "view-logistics" | "contact-seller",
  ) => unknown | Promise<unknown>;
  onDemandAction?: (
    group: MarketV2DemandGroup,
    action: "follow" | "unfollow" | "publish-same" | "start-carpool" | "contact-demanders",
  ) => unknown | Promise<unknown>;
  onWithdraw?: () => unknown | Promise<unknown>;
  onBindBankCard?: () => unknown | Promise<unknown>;
  onManageBankCard?: (card: MarketV2BankCard) => unknown | Promise<unknown>;
  onCreateSurvey?: () => unknown | Promise<unknown>;
  onOpenSurvey?: (survey: MarketV2Survey) => unknown | Promise<unknown>;
  onOpenAfterSaleGroup?: (group: MarketV2AfterSaleGroup) => unknown | Promise<unknown>;
  onManageAfterSaleGroup?: (
    group: MarketV2AfterSaleGroup,
    action: "notice" | "members" | "mute",
  ) => unknown | Promise<unknown>;
}

export interface MarketV2Props extends MarketV2Actions {
  currentUser: User;
  artists: Artist[];
  products: Product[];
  cart: CartItem[];
  buyerOrders: Order[];
  sellerOrders: Order[];
  followedProducts?: Product[];
  followedSellers?: MarketV2SellerProfile[];
  sellerProfiles?: MarketV2SellerProfile[];
  orderViews?: MarketV2OrderView[];
  demandGroups?: MarketV2DemandGroup[];
  wallet?: MarketV2Wallet;
  surveys?: MarketV2Survey[];
  afterSaleGroups?: MarketV2AfterSaleGroup[];
  searchSuggestions?: string[];
  searchStorageKey?: string;
  initialMode?: MarketV2Mode;
  initialPanel?: MarketV2Panel;
  loading?: boolean;
  busyAction?: string | null;
  className?: string;
}

type Notice = { tone: "info" | "error"; text: string };
type ListingFilter = "all" | "product" | "carpool" | "physical" | "virtual";
type BuyerOrderTab = "all" | "pending" | "shipping" | "after-sale";
type SellerOrderTab = "paid" | "shipping" | "refund" | "completed";
type DemandFilter = "all" | "no-supply" | "carpool-ready";
type ReviewFilter = "all" | "positive" | "neutral" | "negative" | "replied";

const money = new Intl.NumberFormat("zh-CN", { style: "currency", currency: "CNY" });
const dayTime = new Intl.DateTimeFormat("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });

const defaultSuggestions = ["演唱会周边", "小卡收纳", "同款配饰", "拼车分摊", "绝版刊物", "应援手幅", "数字纪念票", "线下物料"];

function yuan(cents: number): string {
  return money.format(cents / 100);
}

function shortDate(value?: string | null): string {
  if (!value) return "时间待确认";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "时间待确认" : dayTime.format(date);
}

function safeRecent(storageKey: string): string[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(storageKey) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string").slice(0, 8) : [];
  } catch {
    return [];
  }
}

function dedupeTerms(terms: string[]): string[] {
  const normalized = new Set<string>();
  const result: string[] = [];
  for (const raw of terms) {
    const value = raw.trim();
    const key = value.toLocaleLowerCase("zh-CN");
    if (!value || normalized.has(key)) continue;
    normalized.add(key);
    result.push(value);
    if (result.length === 8) break;
  }
  return result;
}

function initials(value: string): string {
  return [...value.trim()].slice(0, 2).join("") || "同担";
}

function Avatar({ src, name, size = "medium" }: { src?: string | undefined; name: string; size?: "small" | "medium" | "large" }) {
  return (
    <span className={`market-v2__avatar market-v2__avatar--${size}`} aria-label={name}>
      <span>{initials(name)}</span>
      {src ? <img src={src} alt="" loading="lazy" onError={(event) => { event.currentTarget.hidden = true; }} /> : null}
    </span>
  );
}

function IconButton({ label, onClick, children }: { label: string; onClick?: () => void; children: ReactNode }) {
  return <button className="market-v2__icon-button" type="button" aria-label={label} onClick={onClick}>{children}</button>;
}

function EmptyState({ icon: Icon = ShoppingBag, title, body, action }: { icon?: LucideIcon; title: string; body: string; action?: ReactNode }) {
  return (
    <div className="market-v2__empty">
      <span><Icon size={22} /></span>
      <h3>{title}</h3>
      <p>{body}</p>
      {action}
    </div>
  );
}

function SectionTitle({ title, note, action }: { title: string; note?: string; action?: ReactNode }) {
  return (
    <div className="market-v2__section-title">
      <div><h2>{title}</h2>{note ? <p>{note}</p> : null}</div>
      {action}
    </div>
  );
}

function ProductArtwork({ product }: { product: Product }) {
  return (
    <div className="market-v2__artwork">
      <span>{product.isCarpool ? "拼车" : product.type === "virtual" ? "虚拟" : "现货"}</span>
      {product.imageUrl ? <img src={product.imageUrl} alt="" loading="lazy" onError={(event) => { event.currentTarget.hidden = true; }} /> : null}
    </div>
  );
}

function OrderStatus({ status }: { status: Order["status"] }) {
  const labels: Record<Order["status"], string> = {
    pending: "待付款",
    paid: "待发货",
    shipping: "运输中",
    completed: "已完成",
    refunding: "售后中",
    refunded: "已退款",
  };
  return <span className={`market-v2__status market-v2__status--${status}`}>{labels[status]}</span>;
}

export function MarketV2({
  currentUser,
  artists,
  products,
  cart,
  buyerOrders,
  sellerOrders,
  followedProducts = [],
  followedSellers = [],
  sellerProfiles = [],
  orderViews = [],
  demandGroups = [],
  surveys = [],
  afterSaleGroups = [],
  searchSuggestions = defaultSuggestions,
  searchStorageKey = "kira-market-v2-recent-searches",
  initialMode = "buyer",
  initialPanel = "home",
  loading = false,
  busyAction = null,
  className = "",
  wallet,
  onBack,
  onOpenProduct,
  onAddToCart,
  onJoinCarpool,
  onToggleProductFollow,
  onToggleSellerFollow,
  onToggleCartItem,
  onSelectSellerCart,
  onChangeCartQuantity,
  onCheckout,
  onStartProductPublish,
  onStartCarpoolPublish,
  onSellerOrderAction,
  onBuyerOrderAction,
  onDemandAction,
  onWithdraw,
  onBindBankCard,
  onManageBankCard,
  onCreateSurvey,
  onOpenSurvey,
  onOpenAfterSaleGroup,
  onManageAfterSaleGroup,
}: MarketV2Props) {
  const [mode, setMode] = useState<MarketV2Mode>(initialMode);
  const [discovery,setDiscovery]=useState('hot');
  const [selectedArtists,setSelectedArtists]=useState<string[]>([]);
  const [imageIds,setImageIds]=useState<string[]|null>(null);
  const [recentSales,setRecentSales]=useState<Record<string,number>>({});
  useEffect(()=>{let active=true;apiRequest<Record<string,number>>('/api/market/discovery').then(data=>{if(active)setRecentSales(data)}).catch(()=>{});return()=>{active=false}},[]);
  const subscribedArtists=artists.filter(a=>a.isSubscribed);

  const [panel, setPanel] = useState<MarketV2Panel>(initialPanel);
  const [query, setQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [listingFilter, setListingFilter] = useState<ListingFilter>("all");
  const [buyerOrderTab, setBuyerOrderTab] = useState<BuyerOrderTab>("all");
  const [sellerOrderTab, setSellerOrderTab] = useState<SellerOrderTab>("paid");
  const [demandFilter, setDemandFilter] = useState<DemandFilter>("all");
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>("all");
  const [selectedDemandId, setSelectedDemandId] = useState<string | null>(null);
  const [selectedSellerId, setSelectedSellerId] = useState<string | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => safeRecent(searchStorageKey));
  const [notice, setNotice] = useState<Notice | null>(null);
  useEffect(() => { if (!notice) return; const timer = window.setTimeout(() => setNotice(null), notice.tone === "error" ? 10000 : 3500); return () => window.clearTimeout(timer); }, [notice]);
  const [localBusy, setLocalBusy] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(searchStorageKey, JSON.stringify(recentSearches));
    } catch {
      // Storage can be unavailable in private browsing; search remains usable in memory.
    }
  }, [recentSearches, searchStorageKey]);

  const [searchOpen, setSearchOpen] = useState(false);
  const allSuggestions = useMemo(() => dedupeTerms(searchSuggestions), [searchSuggestions]);
  const artistNameById = useMemo(() => new Map(artists.map((artist) => [artist.id, artist.stageName])), [artists]);
  const followedProductIds = useMemo(() => new Set(followedProducts.map((product) => product.id)), [followedProducts]);
  const orderViewById = useMemo(() => new Map(orderViews.map((view) => [view.order.id, view])), [orderViews]);

  const derivedSellers = useMemo(() => {
    const map = new Map<string, MarketV2SellerProfile>();
    for (const profile of sellerProfiles) map.set(profile.id, profile);
    for (const profile of followedSellers) map.set(profile.id, profile);
    for (const product of products) {
      if (!map.has(product.sellerId)) {
        map.set(product.sellerId, {
          id: product.sellerId,
          displayName: product.sellerName,
          completedOrders: product.sales,
        });
      }
    }
    return map;
  }, [followedSellers, products, sellerProfiles]);

  const visibleProducts = useMemo(() => {
    const term = activeQuery.trim().toLocaleLowerCase("zh-CN");
    return products.filter((product) => {
      if (product.status !== "active" || (product.isCarpool && product.deadline && Date.parse(product.deadline)<=Date.now())) return false;
      if(imageIds && !imageIds.includes(product.id)) return false;
      if(discovery==='fans' && !(selectedArtists.length?selectedArtists:subscribedArtists.map(a=>a.id)).includes(product.artistId??'')) return false;
      if (listingFilter === "product" && product.isCarpool) return false;
      if (listingFilter === "carpool" && !product.isCarpool) return false;
      if (listingFilter === "physical" && product.type !== "physical") return false;
      if (listingFilter === "virtual" && product.type !== "virtual") return false;
      if (!term) return true;
      const artistName = product.artistId ? artistNameById.get(product.artistId) ?? "" : "";
      return `${product.title} ${product.description} ${product.sellerName} ${artistName}`.toLocaleLowerCase("zh-CN").includes(term);
    }).sort((a,b)=>{
      if(imageIds) return imageIds.indexOf(a.id)-imageIds.indexOf(b.id);
      if(discovery==='new') return Date.parse(b.createdAt??"1970-01-01")-Date.parse(a.createdAt??"1970-01-01");
      if(discovery==='recommended'){const followed=(p:Product)=>Number(subscribedArtists.some(artist=>artist.id===p.artistId));const delta=followed(b)-followed(a);if(delta)return delta}
      return (recentSales[b.id]??0)-(recentSales[a.id]??0)||Date.parse(b.createdAt??"1970-01-01")-Date.parse(a.createdAt??"1970-01-01");
    });
  }, [activeQuery, artistNameById, listingFilter, products,discovery,selectedArtists,artists,imageIds,recentSales]);

  const cartGroups = useMemo(() => {
    const groups = new Map<string, { sellerId: string; sellerName: string; items: CartItem[] }>();
    for (const item of cart) {
      const group = groups.get(item.sellerId) ?? { sellerId: item.sellerId, sellerName: item.sellerName, items: [] };
      group.items.push(item);
      groups.set(item.sellerId, group);
    }
    return [...groups.values()];
  }, [cart]);

  const selectedCart = cart.filter((item) => item.selected);
  const cartSubtotal = selectedCart.reduce((sum, item) => sum + item.priceCents * item.quantity, 0);
  const cartShipping = useMemo(() => {
    const bySeller = new Map<string, number>();
    for (const item of selectedCart) {
      if (item.type === "virtual") continue;
      bySeller.set(item.sellerId, Math.max(bySeller.get(item.sellerId) ?? 0, item.shippingCents));
    }
    return [...bySeller.values()].reduce((sum, value) => sum + value, 0);
  }, [selectedCart]);

  const buyerTabOrders = buyerOrders.filter((order) => {
    if (buyerOrderTab === "all") return true;
    if (buyerOrderTab === "pending") return order.status === "pending" || order.status === "paid";
    if (buyerOrderTab === "shipping") return order.status === "shipping";
    return order.status === "refunding" || order.status === "refunded";
  });

  const sellerTabOrders = sellerOrders.filter((order) => {
    if (sellerOrderTab === "paid") return order.status === "paid";
    if (sellerOrderTab === "shipping") return order.status === "shipping";
    if (sellerOrderTab === "refund") return order.status === "refunding" || order.status === "refunded";
    return order.status === "completed";
  });

  const visibleDemandGroups = demandGroups.filter((group) => {
    if (demandFilter === "no-supply") return group.activeSellerCount === 0;
    if (demandFilter === "carpool-ready") return group.wishCount >= 3;
    return true;
  });
  const selectedDemand = demandGroups.find((group) => group.id === selectedDemandId) ?? null;
  const selectedSeller = selectedSellerId ? derivedSellers.get(selectedSellerId) ?? null : null;

  async function execute(key: string, label: string, action?: () => unknown | Promise<unknown>) {
    if (!action) {
      setNotice({ tone: "info", text: `“${label}”暂时不可用，请稍后再试。` });
      return;
    }
    if (busyAction || localBusy) return;
    setLocalBusy(key);
    setNotice(null);
    try {
      await action();
      // The action owner reports its actual result; opening a panel is not a successful write.
    } catch (error) {
      setNotice({ tone: "error", text: error instanceof Error ? error.message : `“${label}”未完成，请稍后重试。` });
    } finally {
      setLocalBusy(null);
    }
  }

  function remoteLabel(label: string, available: boolean): ReactNode {
    return <>{label}{available ? null : <small>尚未接入</small>}</>;
  }

  function openSeller(sellerId: string) {
    setSelectedSellerId(sellerId);
    setPanel("seller-profile");
    setReviewFilter("all");
  }

  function submitSearch(event?: FormEvent) {
    event?.preventDefault();
    setSearchOpen(false);
    const normalized = query.trim();
    setActiveQuery(normalized);
    if (normalized) setRecentSearches((current) => dedupeTerms([normalized, ...current]));
  }

  function chooseSearch(term: string) {
    setSearchOpen(false);
    setQuery(term);
    setActiveQuery(term);
    setRecentSearches((current) => dedupeTerms([term, ...current]));
  }

  function renderRemoteButton(
    key: string,
    label: string,
    action: (() => unknown | Promise<unknown>) | undefined,
    className = "market-v2__button market-v2__button--primary",
    disabled = false,
  ) {
    const isBusy = busyAction === key || localBusy === key;
    return (
      <button className={className} type="button" disabled={disabled || Boolean(busyAction || localBusy)} onClick={() => { void execute(key, label, action); }}>
        {isBusy ? "处理中…" : remoteLabel(label, Boolean(action))}
      </button>
    );
  }

  const buyerDashboard = [
    { panel: "cart" as const, icon: ShoppingCart, title: "购物车", note: cart.length ? `${cart.length} 件待决定` : "先把喜欢的放进来", count: cart.length },
    { panel: "carpool" as const, icon: UsersRound, title: "我要上车", note: "一起分摊，不错过限定", count: products.filter((product) => product.isCarpool && product.status === "active").length },
    { panel: "buyer-orders" as const, icon: PackageCheck, title: "我的订单", note: "付款、物流与售后", count: buyerOrders.length },
    { panel: "follows" as const, icon: Heart, title: "我的关注", note: "降价开车及时知道", count: followedProducts.length + followedSellers.length },
  ];

  const sellerDashboard = [
    { panel: "publish" as const, icon: PenLine, title: "我要出物", note: "实物或虚拟物品", count: products.filter((product) => product.sellerId === currentUser.id && product.status === "active").length },
    { panel: "start-carpool" as const, icon: Truck, title: "我要发车", note: "设定截止、库存与退款规则", count: products.filter((product) => product.sellerId === currentUser.id && product.isCarpool).length },
    { panel: "demands" as const, icon: BellRing, title: "需求墙", note: "看看大家正在求什么", count: demandGroups.reduce((sum, group) => sum + group.wishCount, 0) },
    { panel: "seller-orders" as const, icon: ClipboardList, title: "订单管理", note: "发货、退款与评价", count: sellerOrders.filter((order) => order.status === "paid" || order.status === "refunding").length },
  ];

  function renderDashboard() {
    const cards = mode === "buyer" ? buyerDashboard : sellerDashboard;
    if(mode==='seller') return <><section className="market-v2__dashboard market-v2__dashboard--seller"><div className="market-v2__dashboard-grid">{sellerDashboard.map(({panel:target,icon:Icon,title})=><button key={target} className="market-v2__dashboard-card" onClick={()=>setPanel(target)}><span className="market-v2__dashboard-icon"><Icon size={22}/></span><strong>{title}</strong></button>)}</div></section><SellerOverview orders={sellerOrders} onPanel={setPanel}/>{activeQuery||imageIds?<section className="market-ops"><h3>搜索结果</h3>{products.filter(p=>p.sellerId===currentUser.id&&(imageIds?imageIds.includes(p.id):`${p.title} ${p.description}`.includes(activeQuery))).map(p=><button className="market-ops__row" key={p.id} onClick={()=>void onOpenProduct?.(p)}>{p.title}</button>)}{sellerOrders.filter(o=>`${o.id} ${o.items.map(i=>i.title).join(' ')}`.includes(activeQuery)).map(o=><button className="market-ops__row" key={o.id} onClick={()=>setPanel('seller-orders')}>订单 {o.id.slice(-8)} · {yuan(o.totalCents)}</button>)}</section>:null}</>;

    return (
      <>
        <section className="market-v2__discover">


          {query ? <div className="market-v2__search-memory">
            {recentSearches.length ? (
              <div className="market-v2__term-block">
                <div><strong>最近搜索</strong><button type="button" onClick={() => setRecentSearches([])}>一键清除</button></div>
                <div>{recentSearches.map((term) => <button type="button" key={term} onClick={() => chooseSearch(term)}>{term}</button>)}</div>
              </div>
            ) : null}
            <div className="market-v2__term-block">
              <div><strong>猜你想搜</strong><span>根据市集热度推荐</span></div>
              <div>{allSuggestions.map((term) => <button type="button" key={term} onClick={() => chooseSearch(term)}>{term}</button>)}</div>
            </div>
          </div> : null}

        <section className={`market-v2__dashboard market-v2__dashboard--${mode}`}>
          <div className="market-v2__dashboard-grid">
            {cards.map(({ panel: target, icon: Icon, title, note, count }, index) => (
              <button key={target} className={`market-v2__dashboard-card market-v2__dashboard-card--${index + 1}`} type="button" onClick={() => setPanel(target)}>
                <span className="market-v2__dashboard-icon"><Icon size={21} /></span>
                <strong>{title}</strong>
                <small>{note}</small>
                <b>{count}</b>
                <ChevronRight size={16} />
              </button>
            ))}
          </div>
        </section>

          <div className="market-v2__wish-links"><button className="market-v2__wish-entry" type="button" onClick={() => setPanel("wish")}>☆ 许愿池 <ChevronRight size={15}/></button><button className="market-v2__wish-entry" type="button" onClick={()=>setPanel("surveys")}>新品调研 <ChevronRight size={15}/></button></div>
          <div className="market-discovery-tabs" role="tablist" aria-label="商品推荐方式">{[['hot','最近热销'],['new','最近上新'],['fans','同担都在买'],['recommended','好物推荐']].map(([id,title])=><button key={id} role="tab" aria-selected={discovery===id} onClick={()=>setDiscovery(id!)}>{title}</button>)}</div>
          {discovery==='fans'?<div className="market-artist-filter"><small>未勾选时展示全部已订阅艺人的商品</small>{subscribedArtists.length?subscribedArtists.map(a=><label key={a.id}><input type="checkbox" checked={selectedArtists.includes(a.id)} onChange={e=>setSelectedArtists(ids=>e.target.checked?[...ids,a.id]:ids.filter(id=>id!==a.id))}/>{a.stageName}</label>):<p>订阅艺人后，可在这里筛选同担好物。</p>}</div>:null}
          <div className="market-v2__filters" aria-label="商品筛选">
            {([
              ["all", "全部"], ["product", "单件好物"], ["carpool", "拼车"], ["physical", "实物"], ["virtual", "虚拟物品"],
            ] as const).map(([value, label]) => (
              <button type="button" key={value} className={listingFilter === value ? "is-active" : ""} onClick={() => setListingFilter(value)}>{label}</button>
            ))}
          </div>

          {loading ? <div className="market-v2__loading"><span /><span /><span /><p>正在整理市集好物…</p></div> : null}
          {!loading && !visibleProducts.length ? (
            <EmptyState icon={Search} title="没有找到合适的好物" body="换个关键词或筛选条件，再逛一圈吧。" />
          ) : null}
          {!loading && visibleProducts.length ? (
            <div className="market-v2__product-grid">
              {visibleProducts.map((product) => {
                const followed = followedProductIds.has(product.id);
                return (
                  <article className="market-v2__product-card" key={product.id}>
                    <button className="market-v2__product-open" type="button" onClick={() => { void execute(`product:${product.id}`, "查看商品", onOpenProduct ? () => onOpenProduct(product) : undefined); }}>
                      <ProductArtwork product={product} />
                      <div className="market-v2__product-copy">
                        <span>{product.artistId ? artistNameById.get(product.artistId) ?? "同担好物" : "同担好物"}</span>
                        <h3>{product.title}</h3>
                        <p>{product.description}</p>
                        {product.isCarpool ? <small><Clock3 size={13} />{shortDate(product.deadline)}截止 · 剩 {product.stock} 份</small> : <small>{product.type === "virtual" ? "线上交付" : `库存 ${product.stock}`} · 已售 {product.sales}</small>}
                      </div>
                    </button>
                    <div className="market-v2__product-seller">
                      <button type="button" onClick={() => openSeller(product.sellerId)}>{product.sellerName}<ChevronRight size={13} /></button>
                      <button type="button" className={followed ? "is-active" : ""} onClick={() => { void execute(`follow-product:${product.id}`, followed ? "取消关注" : "关注商品", onToggleProductFollow ? () => onToggleProductFollow(product, !followed) : undefined); }} aria-label={followed ? "取消关注商品" : "关注商品"}><Heart size={15} fill={followed ? "currentColor" : "none"} /></button>
                    </div>
                    <div className="market-v2__product-buy">
                      <div><strong>{yuan(product.priceCents)}</strong><span>{product.shippingCents ? `另含运费 ${yuan(product.shippingCents)}` : "免运费"}</span></div>
                      <button type="button" onClick={() => { const callback = product.isCarpool ? onJoinCarpool : onAddToCart; void execute(`${product.isCarpool ? "join" : "cart"}:${product.id}`, product.isCarpool ? "立即上车" : "加入购物车", callback ? () => callback(product) : undefined); }}>
                        {remoteLabel(product.isCarpool ? "上车" : "加购", Boolean(product.isCarpool ? onJoinCarpool : onAddToCart))}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : null}
        </section>
      </>
    );
  }

  function renderCart() {
    return (
      <section className="market-v2__panel-body">
        <SectionTitle title="购物车" note="按卖家合并运费，结算前可自由勾选" />
        {!cartGroups.length ? <EmptyState icon={ShoppingCart} title="购物车还是空的" body="去逛逛，把让你心动的好物先放进来。" action={<button type="button" className="market-v2__button market-v2__button--soft" onClick={() => setPanel("home")}>去逛市集</button>} /> : null}
        <div className="market-v2__cart-groups">
          {cartGroups.map((group) => {
            const selected = group.items.length > 0 && group.items.every((item) => item.selected);
            const selectedItems = group.items.filter((item) => item.selected);
            const subtotal = selectedItems.reduce((sum, item) => sum + item.priceCents * item.quantity, 0);
            const shipping = selectedItems.reduce((maximum, item) => item.type === "virtual" ? maximum : Math.max(maximum, item.shippingCents), 0);
            return (
              <article className="market-v2__cart-group" key={group.sellerId}>
                <div className="market-v2__cart-seller">
                  <button className={`market-v2__check ${selected ? "is-checked" : ""}`} type="button" aria-label={selected ? "取消勾选该卖家商品" : "勾选该卖家商品"} onClick={() => { void execute(`select-seller:${group.sellerId}`, "选择店铺商品", onSelectSellerCart ? () => onSelectSellerCart(group.sellerId, !selected) : undefined); }}>{selected ? <Check size={13} /> : null}</button>
                  <button type="button" onClick={() => openSeller(group.sellerId)}><Store size={15} />{group.sellerName}<ChevronRight size={13} /></button>
                </div>
                {group.items.map((item) => (
                  <div className="market-v2__cart-item" key={item.id}>
                    <button className={`market-v2__check ${item.selected ? "is-checked" : ""}`} type="button" aria-label={item.selected ? "取消勾选商品" : "勾选商品"} onClick={() => { void execute(`select-cart:${item.id}`, "选择商品", onToggleCartItem ? () => onToggleCartItem(item.id, !item.selected) : undefined); }}>{item.selected ? <Check size={13} /> : null}</button>
                    <ProductArtwork product={item} />
                    <div className="market-v2__cart-item-copy">
                      <strong>{item.title}</strong>
                      <span>{item.type === "virtual" ? "虚拟物品 · 线上交付" : item.shippingCents ? `运费基准 ${yuan(item.shippingCents)}` : "免运费"}</span>
                      <b>{yuan(item.priceCents)}</b>
                    </div>
                    <div className="market-v2__stepper">
                      <button type="button" disabled={item.quantity <= 1 || Boolean(busyAction || localBusy)} onClick={() => { void execute(`quantity:${item.id}`, "减少数量", onChangeCartQuantity ? () => onChangeCartQuantity(item.id, item.quantity - 1) : undefined); }}>−</button>
                      <span>{item.quantity}</span>
                      <button type="button" disabled={item.quantity >= item.stock || Boolean(busyAction || localBusy)} onClick={() => { void execute(`quantity:${item.id}`, "增加数量", onChangeCartQuantity ? () => onChangeCartQuantity(item.id, item.quantity + 1) : undefined); }}>＋</button>
                    </div>
                  </div>
                ))}
                <div className="market-v2__cart-breakdown"><span>商品 {yuan(subtotal)}</span><span>本店运费 {yuan(shipping)}</span><strong>本店到手 {yuan(subtotal + shipping)}</strong></div>
              </article>
            );
          })}
        </div>
        {cart.length ? (
          <div className="market-v2__checkout-bar">
            <div><span>商品 {yuan(cartSubtotal)} + 运费 {yuan(cartShipping)}</span><strong>到手价 {yuan(cartSubtotal + cartShipping)}</strong></div>
            {renderRemoteButton("checkout", selectedCart.length ? `演示结算 ${selectedCart.length} 件` : "请选择商品", onCheckout && selectedCart.length ? () => onCheckout(selectedCart) : undefined, "market-v2__button market-v2__button--primary", !selectedCart.length)}
          </div>
        ) : null}
      </section>
    );
  }

  function renderCarpool() {
    const carpools = products.filter((product) => product.isCarpool && product.status === "active");
    return (
      <section className="market-v2__panel-body">
        <SectionTitle title="一起上车" note="截止时间、可退规则和剩余份数都为你标清" />
        {!carpools.length ? <EmptyState icon={Truck} title="暂时没有可上的车" body="可以先关注心愿商品，开车时及时收到消息。" /> : null}
        <div className="market-v2__carpool-list">
          {carpools.map((product) => (
            <article className="market-v2__carpool-card" key={product.id}>
              <ProductArtwork product={product} />
              <div className="market-v2__carpool-copy">
                <span>{product.refundAllowed ? "截止前可申请退款" : "成团后不支持退款"}</span>
                <h3>{product.title}</h3>
                <p>{product.description}</p>
                <div><Clock3 size={14} /><strong>{shortDate(product.deadline)}</strong><small>截止</small></div>
                <div className="market-v2__carpool-meter"><i style={{ width: `${Math.min(100, Math.max(8, (product.sales / Math.max(1, product.sales + product.stock)) * 100))}%` }} /><span>已上 {product.sales} 份 · 剩 {product.stock} 份</span></div>
                <footer><div><b>{yuan(product.priceCents)}</b><span>到手约 {yuan(product.priceCents + product.shippingCents)}</span></div>{renderRemoteButton(`join:${product.id}`, "立即上车", onJoinCarpool ? () => onJoinCarpool(product) : undefined)}</footer>
              </div>
            </article>
          ))}
        </div>
      </section>
    );
  }

  function renderOrderCard(order: Order, sellerSide: boolean) {
    const view = orderViewById.get(order.id);
    let action: ReactNode = null;
    if (sellerSide) {
      if (order.status === "paid") action = renderRemoteButton(`ship:${order.id}`, "去发货", onSellerOrderAction ? () => onSellerOrderAction(order, "ship") : undefined);
      else if (order.status === "refunding") action = renderRemoteButton(`refund:${order.id}`, "处理退款", onSellerOrderAction ? () => onSellerOrderAction(order, "handle-refund") : undefined);
      else if (order.status === "shipping") action = renderRemoteButton(`logistics:${order.id}`, "查看物流", onSellerOrderAction ? () => onSellerOrderAction(order, "view-logistics") : undefined, "market-v2__button market-v2__button--soft");
      else action = renderRemoteButton(`contact:${order.id}`, "查看详情", onSellerOrderAction ? () => onSellerOrderAction(order, "contact-buyer") : undefined, "market-v2__button market-v2__button--soft");
    } else {
      if (order.status === "pending") action = renderRemoteButton(`pay:${order.id}`, "查看详情", onBuyerOrderAction ? () => onBuyerOrderAction(order, "view-logistics") : undefined);
      else if (order.status === "shipping") action = renderRemoteButton(`confirm:${order.id}`, "确认收货", onBuyerOrderAction ? () => onBuyerOrderAction(order, "confirm") : undefined);
      else if (order.status === "completed" && !order.review) action = renderRemoteButton(`review:${order.id}`, "去评价", onBuyerOrderAction ? () => onBuyerOrderAction(order, "review") : undefined);
      else if (order.status === "refunding" || order.status === "refunded") action = renderRemoteButton(`contact:${order.id}`, "查看详情", onBuyerOrderAction ? () => onBuyerOrderAction(order, "contact-seller") : undefined, "market-v2__button market-v2__button--soft");
      else action = renderRemoteButton(`logistics:${order.id}`, "查看详情", onBuyerOrderAction ? () => onBuyerOrderAction(order, "view-logistics") : undefined, "market-v2__button market-v2__button--soft");
    }
    return (
      <article className="market-v2__order-card" key={order.id}>
        <header><div><span>{sellerSide ? `买家：${order.buyerName}` : order.sellerName}</span><small>订单 {order.id}</small></div><OrderStatus status={order.status} /></header>
        <div className="market-v2__order-items">
          {order.items.map((item) => <div key={`${order.id}:${item.productId}`}><span>{item.title}</span><small>× {item.quantity}</small><strong>{yuan(item.unitPriceCents * item.quantity)}</strong></div>)}
        </div>
        {order.refundReason ? <p className="market-v2__order-alert">退款原因：{order.refundReason}</p> : null}{order.refundResolution ? <p className="market-v2__order-alert">处理说明：{order.refundResolution}</p> : null}{order.review ? <p className="market-v2__order-alert">评价 {order.review.rating} 星：{order.review.content}</p> : null}
        {view?.trackingEvents?.[0] ? <p className="market-v2__order-track"><Truck size={14} />{view.trackingEvents[0].content} · {shortDate(view.trackingEvents[0].occurredAt)}</p> : null}
        <footer><div><span>运费 {yuan(order.shippingCents)}</span><strong>合计 {yuan(order.totalCents)}</strong></div>{action}{!sellerSide && order.refundEligible ? renderRemoteButton(`request-refund:${order.id}`, "申请退款", onBuyerOrderAction ? () => onBuyerOrderAction(order, "refund") : undefined, "market-v2__button market-v2__button--soft") : null}</footer>
      </article>
    );
  }

  function renderBuyerOrders() {
    const tabs: Array<{ value: BuyerOrderTab; label: string; count: number }> = [
      { value: "all", label: "全部", count: buyerOrders.length },
      { value: "pending", label: "待处理", count: buyerOrders.filter((order) => order.status === "pending" || order.status === "paid").length },
      { value: "shipping", label: "运输中", count: buyerOrders.filter((order) => order.status === "shipping").length },
      { value: "after-sale", label: "退款售后", count: buyerOrders.filter((order) => order.status === "refunding" || order.status === "refunded").length },
    ];
    return (
      <section className="market-v2__panel-body">
        <SectionTitle title="我的订单" note="付款、物流、评价和售后都在这里" action={<button type="button" onClick={()=>setPanel("after-sale")}>售后会话</button>}/>
        <div className="market-v2__tabs market-v2__tabs--four">{tabs.map((tab) => <button key={tab.value} type="button" className={buyerOrderTab === tab.value ? "is-active" : ""} onClick={() => setBuyerOrderTab(tab.value)}><span>{tab.label}</span><b>{tab.count}</b></button>)}</div>
        {!buyerTabOrders.length ? <EmptyState icon={PackageCheck} title="这个状态下没有订单" body="新的交易进度会自动出现在这里。" /> : <div className="market-v2__order-list">{buyerTabOrders.map((order) => renderOrderCard(order, false))}</div>}
      </section>
    );
  }

  function renderFollows() {
    return (
      <section className="market-v2__panel-body">
        <SectionTitle title="我的关注" note="关注的商品与靠谱卖家" />
        {!followedProducts.length && !followedSellers.length ? <EmptyState icon={Heart} title="还没有关注" body="遇见喜欢的好物或卖家，点一下心形就能留在这里。" /> : null}
        {followedSellers.length ? <><h3 className="market-v2__subheading">关注的卖家</h3><div className="market-v2__followed-sellers">{followedSellers.map((seller) => <button key={seller.id} type="button" onClick={() => openSeller(seller.id)}><Avatar src={seller.avatarUrl} name={seller.displayName} /><span><strong>{seller.displayName}</strong><small>{seller.completedOrders ?? 0} 笔顺利成交</small></span><ChevronRight size={16} /></button>)}</div></> : null}
        {followedProducts.length ? <><h3 className="market-v2__subheading">关注的商品</h3><div className="market-v2__compact-products">{followedProducts.map((product) => <article key={product.id}><ProductArtwork product={product} /><div><strong>{product.title}</strong><span>{product.sellerName}</span><b>{yuan(product.priceCents)}</b></div>{renderRemoteButton(`open:${product.id}`, "查看", onOpenProduct ? () => onOpenProduct(product) : undefined, "market-v2__button market-v2__button--soft")}</article>)}</div></> : null}
      </section>
    );
  }

  function renderPublish() {
    const ownItems = products.filter((product) => product.sellerId === currentUser.id && !product.isCarpool);
    return (
      <section className="market-v2__panel-body">
        <SectionTitle title="我要出物" note="先选交付方式，再完善图文、库存与价格" />
        <div className="market-v2__publish-choices">
          <button type="button" onClick={() => { void execute("publish:physical", "发布实物", onStartProductPublish ? () => onStartProductPublish("physical") : undefined); }}><span><ShoppingBag size={22} /></span><strong>发布实物</strong><p>周边、小卡、刊物等需要寄送的物品</p><small>{onStartProductPublish ? "填写商品信息" : "暂不可用"}<ChevronRight size={14} /></small></button>
          <button type="button" onClick={() => { void execute("publish:virtual", "发布虚拟物品", onStartProductPublish ? () => onStartProductPublish("virtual") : undefined); }}><span><Sparkles size={22} /></span><strong>发布虚拟物品</strong><p>电子票根、数字纪念品等线上交付内容</p><small>{onStartProductPublish ? "填写商品信息" : "暂不可用"}<ChevronRight size={14} /></small></button>
        </div>
        <div className="market-v2__safety-note"><ShieldCheck size={18} /><p><strong>发布前请确认</strong><span>如实描述品相与授权范围；虚拟物品需注明交付方式；实物请填写库存和运费。</span></p></div>
        <SectionTitle title="我在卖的" note={`${ownItems.filter((item) => item.status === "active").length} 件展示中`} />
        {!ownItems.length ? <EmptyState icon={Store} title="还没有发布过物品" body="选择上方类型，完成第一件物品的发布。" /> : <div className="market-v2__compact-products">{ownItems.map((product) => <article key={product.id}><ProductArtwork product={product} /><div><strong>{product.title}</strong><span>{product.status === "active" ? `展示中 · 库存 ${product.stock}` : `已下架${product.closeReason === "expired" ? " · 已到期" : ""}`}</span><b>{yuan(product.priceCents)}</b></div>{renderRemoteButton(`open:${product.id}`, "管理", onOpenProduct ? () => onOpenProduct(product) : undefined, "market-v2__button market-v2__button--soft")}</article>)}</div>}
      </section>
    );
  }

  function renderStartCarpool() {
    const ownCarpools = products.filter((product) => product.sellerId === currentUser.id && product.isCarpool);
    return (
      <section className="market-v2__panel-body">
        <SectionTitle title="我要发车" note="先把截止、改期、退款和库存规则说清楚" />
        <div className="market-v2__carpool-rules">
          {["截止时间与预计发货日", "改期次数与通知方式", "成团前后的退款规则", "总库存与每人限购数"].map((rule, index) => <div key={rule}><span>{index + 1}</span><p>{rule}</p><Check size={15} /></div>)}
        </div>
        <div className="market-v2__launch-card"><div><Truck size={24} /><span>新建一趟拼车</span></div><h3>规则透明，大家才敢放心上车。</h3><p>创建页应收集商品信息、份数、截团时间、改期和退款约定。</p>{renderRemoteButton("start-carpool", "填写拼车信息", onStartCarpoolPublish ? () => onStartCarpoolPublish() : undefined)}</div>
        <SectionTitle title="我的拼车" note={`${ownCarpools.length} 趟`} />
        {!ownCarpools.length ? <EmptyState icon={Truck} title="还没有发过车" body="需求墙里也许正有人等你发起。" /> : <div className="market-v2__compact-products">{ownCarpools.map((product) => <article key={product.id}><ProductArtwork product={product} /><div><strong>{product.title}</strong><span>{shortDate(product.deadline)}截止 · 剩 {product.stock} 份</span><b>{yuan(product.priceCents)}</b></div>{renderRemoteButton(`manage-carpool:${product.id}`, "管理", onStartCarpoolPublish ? () => onStartCarpoolPublish(product) : undefined, "market-v2__button market-v2__button--soft")}</article>)}</div>}
      </section>
    );
  }

  function renderLegacyDemands() {
    if (selectedDemand) {
      return (
        <section className="market-v2__panel-body">
          <button className="market-v2__inline-back" type="button" onClick={() => setSelectedDemandId(null)}><ArrowLeft size={16} />返回需求墙</button>
          <div className="market-v2__demand-hero"><div>{selectedDemand.coverUrl ? <img src={selectedDemand.coverUrl} alt="" /> : <BellRing size={25} />}</div><span>{selectedDemand.artistName}</span><h2>{selectedDemand.title}</h2><p>{selectedDemand.wishCount} 人想要 · 心理价中位数 {yuan(selectedDemand.medianBudgetCents)}</p></div>
          <div className="market-v2__demand-actions">
            {renderRemoteButton(`demand-follow:${selectedDemand.id}`, selectedDemand.isFollowed ? "取消跟进" : "跟进需求", onDemandAction ? () => onDemandAction(selectedDemand, selectedDemand.isFollowed ? "unfollow" : "follow") : undefined)}
            {renderRemoteButton(`demand-publish:${selectedDemand.id}`, "发布同款", onDemandAction ? () => onDemandAction(selectedDemand, "publish-same") : undefined, "market-v2__button market-v2__button--soft")}
            {renderRemoteButton(`demand-carpool:${selectedDemand.id}`, "发起拼车", onDemandAction ? () => onDemandAction(selectedDemand, "start-carpool") : undefined, "market-v2__button market-v2__button--soft")}
          </div>
          <SectionTitle title="具体心愿" note="隐去完整身份，仅展示交易必要信息" />
          {!selectedDemand.wishes.length ? <EmptyState icon={BellRing} title="还没有具体心愿" body="有新的需求后会显示在这里。" /> : <div className="market-v2__wish-list">{selectedDemand.wishes.map((wish) => <article key={wish.id}><Avatar name={wish.authorNameMasked} size="small" /><div><strong>{wish.authorNameMasked}<span>{wish.status === "fulfilled" ? "已满足" : wish.status === "followed" ? "已跟进" : "等待中"}</span></strong><p>{wish.note || "希望品相良好，细节可沟通。"}</p><small>预算 {yuan(wish.budgetCents)} · {wish.quantity} 件 · {shortDate(wish.createdAt)}</small></div></article>)}</div>}
          {renderRemoteButton(`contact-demanders:${selectedDemand.id}`, "联系有需求的同担", onDemandAction ? () => onDemandAction(selectedDemand, "contact-demanders") : undefined, "market-v2__button market-v2__button--wide")}
        </section>
      );
    }
    return (
      <section className="market-v2__panel-body">
        <SectionTitle title="需求墙" note="同类心愿已聚合，先看真实需求再决定上新" />
        <div className="market-v2__filters" aria-label="需求筛选">{([ ["all", "全部需求"], ["no-supply", "暂无供给"], ["carpool-ready", "适合拼车"] ] as const).map(([value, label]) => <button type="button" key={value} className={demandFilter === value ? "is-active" : ""} onClick={() => setDemandFilter(value)}>{label}</button>)}</div>
        {!visibleDemandGroups.length ? <EmptyState icon={BellRing} title="当前筛选下没有需求" body="换一个条件看看，或等待新的心愿汇入。" /> : <div className="market-v2__demand-grid">{visibleDemandGroups.map((group) => <button type="button" key={group.id} onClick={() => setSelectedDemandId(group.id)}><div>{group.coverUrl ? <img src={group.coverUrl} alt="" loading="lazy" /> : <BellRing size={22} />}</div><span>{group.artistName}</span><h3>{group.title}</h3><p><strong>{group.wishCount}</strong> 人想要 · 预算中位数 {yuan(group.medianBudgetCents)}</p><footer><small>{group.activeSellerCount ? `${group.activeSellerCount} 位卖家跟进` : "暂无供给"}</small><ChevronRight size={15} /></footer></button>)}</div>}
      </section>
    );
  }

  function renderSellerOrders() {
    const tabs: Array<{ value: SellerOrderTab; label: string; count: number }> = [
      { value: "paid", label: "待发货", count: sellerOrders.filter((order) => order.status === "paid").length },
      { value: "shipping", label: "已发货", count: sellerOrders.filter((order) => order.status === "shipping").length },
      { value: "refund", label: "退款售后", count: sellerOrders.filter((order) => order.status === "refunding" || order.status === "refunded").length },
      { value: "completed", label: "已完成", count: sellerOrders.filter((order) => order.status === "completed").length },
    ];
    return (
      <section className="market-v2__panel-body">
        <SectionTitle title="订单管理" note="按进度处理，不漏掉一笔交付" />
        <div className="market-v2__tabs market-v2__tabs--four">{tabs.map((tab) => <button key={tab.value} type="button" className={sellerOrderTab === tab.value ? "is-active" : ""} onClick={() => setSellerOrderTab(tab.value)}><span>{tab.label}</span><b>{tab.count}</b></button>)}</div>
        {!sellerTabOrders.length ? <EmptyState icon={ClipboardList} title="这个状态下没有订单" body="新的订单进度会自动汇入对应分类。" /> : <div className="market-v2__order-list">{sellerTabOrders.map((order) => renderOrderCard(order, true))}</div>}
      </section>
    );
  }

  function renderWallet() {
    return (
      <section className="market-v2__panel-body">
        <SectionTitle title="钱包" note="收入、结算中资金与银行卡" />
        {!wallet ? <EmptyState icon={WalletCards} title="钱包暂不可用" body="请稍后再来查看余额和账单。" action={renderRemoteButton("bind-card", "添加银行卡", onBindBankCard, "market-v2__button market-v2__button--soft")} /> : (
          <>
            <div className="market-v2__wallet-card"><span>可提现余额</span><strong>{yuan(wallet.availableCents)}</strong><div><p>结算中<b>{yuan(wallet.pendingCents)}</b></p><p>冻结中<b>{yuan(wallet.frozenCents)}</b></p></div>{renderRemoteButton("withdraw", "申请提现", onWithdraw, "market-v2__button market-v2__button--light", wallet.availableCents <= 0)}</div>
            <SectionTitle title="银行卡" action={renderRemoteButton("bind-card", "添加", onBindBankCard, "market-v2__text-button")} />
            {!wallet.bankCards.length ? <EmptyState icon={CreditCard} title="还没有银行卡" body="绑定后可用于安全提现，敏感信息不在页面保存。" /> : <div className="market-v2__bank-list">{wallet.bankCards.map((card) => <button type="button" key={card.id} onClick={() => { void execute(`card:${card.id}`, "管理银行卡", onManageBankCard ? () => onManageBankCard(card) : undefined); }}><span><CreditCard size={20} /></span><p><strong>{card.bankName}{card.isDefault ? <small>默认</small> : null}</strong><b>尾号 {card.lastFour}</b></p><ChevronRight size={16} /></button>)}</div>}
            <SectionTitle title="近期账单" />
            {!wallet.recentEntries?.length ? <EmptyState icon={Banknote} title="暂无账单" body="成交和提现记录会在这里按时间展示。" /> : <div className="market-v2__ledger">{wallet.recentEntries.map((entry) => <div key={entry.id}><span><strong>{entry.title}</strong><small>{shortDate(entry.createdAt)}</small></span><b className={entry.direction === "income" ? "is-income" : ""}>{entry.direction === "income" ? "+" : "−"}{yuan(entry.amountCents)}</b></div>)}</div>}
          </>
        )}
      </section>
    );
  }

  function renderSurveys() {
    const statusLabel: Record<MarketV2Survey["status"], string> = { draft: "草稿", active: "收集中", closed: "已结束" };
    return (
      <section className="market-v2__panel-body">
        <SectionTitle title="新品调研" note="上新前先问问同担，减少盲目备货" action={renderRemoteButton("create-survey", "新建调研", onCreateSurvey, "market-v2__text-button")} />
        <div className="market-v2__survey-guide"><Sparkles size={20} /><div><strong>一份好调研，可以问这些</strong><p>款式偏好、可接受价格、预计数量、是否愿意拼车。</p></div></div>
        {!surveys.length ? <EmptyState icon={Sparkles} title="还没有新品调研" body="先发布一份简短问卷，看看大家真正想要什么。" /> : <div className="market-v2__survey-list">{surveys.map((survey) => <button type="button" key={survey.id} onClick={() => { void execute(`survey:${survey.id}`, "打开调研", onOpenSurvey ? () => onOpenSurvey(survey) : undefined); }}><span>{statusLabel[survey.status]}</span><h3>{survey.title}</h3><p>{survey.description || "暂无补充说明"}</p><footer><small>{survey.responseCount} 份回答 · 更新于 {shortDate(survey.updatedAt)}</small><ChevronRight size={16} /></footer></button>)}</div>}
      </section>
    );
  }

  function renderAfterSale() {
    return (
      <section className="market-v2__panel-body">
        <SectionTitle title="售后群" note="按拼车或订单批次沟通通知、物流与问题" />
        <div className="market-v2__safety-note"><ShieldCheck size={18} /><p><strong>群聊只用于交易售后</strong><span>关键承诺仍应同步到订单售后记录，避免仅凭聊天处理退款。</span></p></div>
        {!afterSaleGroups.length ? <EmptyState icon={MessageCircleMore} title="还没有售后群" body="有相关拼车或订单后会显示在这里。" /> : <div className="market-v2__group-list">{afterSaleGroups.map((group) => <article key={group.id}><header><div><MessageCircleMore size={19} /></div><span><strong>{group.name}</strong><small>{group.relatedTitle} · {group.memberCount} 人</small></span><b>{group.muted ? "已免打扰" : "沟通中"}</b></header>{group.notice ? <p><BellRing size={14} />{group.notice}</p> : null}{group.messages?.[0] ? <div className="market-v2__group-preview"><strong>{group.messages[0].authorName}</strong><span>{group.messages[0].content}</span><small>{shortDate(group.messages[0].createdAt)}</small></div> : <div className="market-v2__group-preview"><span>暂无新消息</span></div>}<footer>{renderRemoteButton(`open-group:${group.id}`, "进入群聊", onOpenAfterSaleGroup ? () => onOpenAfterSaleGroup(group) : undefined)}{group.isOwner ? renderRemoteButton(`notice-group:${group.id}`, "管理群公告", onManageAfterSaleGroup ? () => onManageAfterSaleGroup(group, "notice") : undefined, "market-v2__button market-v2__button--soft") : null}</footer></article>)}</div>}
      </section>
    );
  }

  function renderSellerProfile() {
    if (!selectedSeller) return <section className="market-v2__panel-body"><EmptyState icon={Store} title="没有找到卖家资料" body="请返回市集重新选择商品。" /></section>;
    const reviews = selectedSeller.reviews ?? [];
    const visibleReviews = reviews.filter((review) => {
      if (reviewFilter === "positive") return review.rating >= 4;
      if (reviewFilter === "neutral") return review.rating === 3;
      if (reviewFilter === "negative") return review.rating <= 2;
      if (reviewFilter === "replied") return Boolean(review.sellerReply);
      return true;
    });
    const sellerProducts = products.filter((product) => product.sellerId === selectedSeller.id && product.status === "active");
    const followed = selectedSeller.isFollowed ?? followedSellers.some((seller) => seller.id === selectedSeller.id);
    return (
      <section className="market-v2__panel-body market-v2__seller-profile">
        <div className="market-v2__seller-hero"><Avatar src={selectedSeller.avatarUrl} name={selectedSeller.displayName} size="large" /><div><h2>{selectedSeller.displayName}{selectedSeller.verified ? <BadgeCheck size={17} /> : null}</h2><p>{selectedSeller.bio || "这个卖家还没有填写介绍。"}</p></div>{renderRemoteButton(`follow-seller:${selectedSeller.id}`, followed ? "已关注" : "关注卖家", onToggleSellerFollow ? () => onToggleSellerFollow(selectedSeller, !followed) : undefined, followed ? "market-v2__button market-v2__button--soft" : "market-v2__button market-v2__button--primary")}</div>
        <div className="market-v2__seller-metrics"><div><strong>{selectedSeller.rating?.toFixed(1) ?? "—"}</strong><span>综合评分</span></div><div><strong>{selectedSeller.positiveRate === undefined ? "—" : `${selectedSeller.positiveRate}%`}</strong><span>好评率</span></div><div><strong>{selectedSeller.completedOrders ?? 0}</strong><span>顺利成交</span></div><div><strong>{selectedSeller.followerCount ?? 0}</strong><span>关注者</span></div></div>
        {selectedSeller.reviewTags?.length ? <div className="market-v2__review-tags">{selectedSeller.reviewTags.map((tag) => <span key={tag.label}>{tag.label}<b>{tag.count}</b></span>)}</div> : null}
        <SectionTitle title="正在出售" note={`${sellerProducts.length} 件`} />
        {!sellerProducts.length ? <EmptyState icon={ShoppingBag} title="暂时没有在售商品" body="可以先关注卖家，等待下一次上新。" /> : <div className="market-v2__compact-products">{sellerProducts.map((product) => <article key={product.id}><ProductArtwork product={product} /><div><strong>{product.title}</strong><span>{product.isCarpool ? "拼车中" : product.type === "virtual" ? "虚拟物品" : "实物"}</span><b>{yuan(product.priceCents)}</b></div>{renderRemoteButton(`open:${product.id}`, "查看", onOpenProduct ? () => onOpenProduct(product) : undefined, "market-v2__button market-v2__button--soft")}</article>)}</div>}
        <SectionTitle title="交易评价" note={`${reviews.length} 条`} />
        <div className="market-v2__filters" aria-label="评价筛选">{([ ["all", "全部"], ["positive", "好评"], ["neutral", "中评"], ["negative", "差评"], ["replied", "有回复"] ] as const).map(([value, label]) => <button type="button" key={value} className={reviewFilter === value ? "is-active" : ""} onClick={() => setReviewFilter(value)}>{label}</button>)}</div>
        {!visibleReviews.length ? <EmptyState icon={Star} title="暂无符合条件的评价" body="完成交易后的真实评价会展示在这里。" /> : <div className="market-v2__review-list">{visibleReviews.map((review) => <article key={review.id}><header><Avatar src={review.authorAvatarUrl} name={review.authorName} size="small" /><div><strong>{review.authorName}</strong><span>{Array.from({ length: 5 }, (_, index) => <Star key={index} size={12} fill={index < review.rating ? "currentColor" : "none"} />)}</span></div><small>{shortDate(review.createdAt)}</small></header><p>{review.content}</p>{review.tags?.length ? <div>{review.tags.map((tag) => <span key={tag}>{tag}</span>)}</div> : null}{review.sellerReply ? <blockquote><strong>卖家回复</strong>{review.sellerReply}</blockquote> : null}</article>)}</div>}
      </section>
    );
  }

  function renderPanel() {
    if (panel === "wish") return <WishPool artists={artists}/>;
    if (panel === "demands") return <DemandWall onPublish={carpool=>carpool?onStartCarpoolPublish?.():onStartProductPublish?.('physical')}/>;
    if (panel === "home") return renderDashboard();
    if (panel === "cart") return renderCart();
    if (panel === "carpool") return renderCarpool();
    if (panel === "buyer-orders") return renderBuyerOrders();
    if (panel === "follows") return renderFollows();
    if (panel === "publish") return renderPublish();
    if (panel === "start-carpool") return <CarpoolManagement onCreate={()=>onStartCarpoolPublish?.()}/>;

    if (panel === "seller-orders") return renderSellerOrders();
    if (panel === "wallet") return <SellerWallet/>;
    if (panel === "surveys") return <MarketSurveys seller={mode==="seller"}/>;
    if (panel === "after-sale") return <AfterSaleGroups/>;
    return renderSellerProfile();
  }

  return (
    <div className={`market-v2 ${panel === "home" ? "market-v2--home" : ""} ${className}`}>
      <header className="market-v2__topbar">
        {panel !== "home" ? <IconButton label="返回市集首页" onClick={() => { setPanel("home"); setSelectedDemandId(null); }}><ArrowLeft size={20} /></IconButton> : onBack ? <IconButton label="返回" onClick={onBack}><ArrowLeft size={20} /></IconButton> : <span className="market-v2__topbar-mark"><ShoppingBag size={18} /></span>}
        <div><span>淘好物，也安心出物</span><h1>{panel === "seller-profile" ? "卖家主页" : "同担市集"}</h1></div>
        <IconButton label="打开购物车" onClick={() => setPanel("cart")}><ShoppingCart size={20} />{cart.length ? <b>{Math.min(99, cart.length)}</b> : null}</IconButton>
      </header>

      <div className="market-identity-row">
        <div className="market-identity"><span className="market-identity__avatar"><span>{currentUser.displayName.slice(0,1)}</span>{currentUser.avatarUrl ? <img src={currentUser.avatarUrl} alt="" onError={e=>{e.currentTarget.style.display="none"}}/> : null}</span><div><strong>{currentUser.displayName}</strong><small>当前身份 <em>{mode === "buyer" ? "买家" : "卖家"}</em></small></div></div>
        <div className="market-v2__mode-switch" role="tablist" aria-label="市集身份">
          <button type="button" role="tab" aria-label="我是买家" aria-selected={mode === "buyer"} className={mode === "buyer" ? "is-active" : ""} onClick={() => { setMode("buyer"); setPanel("home"); setSearchOpen(false); }}><Search size={18}/>出么老师</button>
          <button type="button" role="tab" aria-label="我是卖家" aria-selected={mode === "seller"} className={mode === "seller" ? "is-active" : ""} onClick={() => { setMode("seller"); setPanel("home"); setSearchOpen(false); }}><Tag size={18}/>我要出</button>
        </div>
        <button className="market-publish-add" type="button" aria-label="发布商品" onClick={()=>{setMode("seller");setPanel("publish");setSearchOpen(false)}}><Plus size={22}/></button>
      </div>
      {panel === "home" ? <div className="market-search-row"><form className="market-v2__search" onSubmit={submitSearch}>
        <Search size={20}/><input value={query} onFocus={()=>setSearchOpen(true)} onKeyDown={e=>{if(e.key==='Escape')setSearchOpen(false)}} onChange={e=>{setQuery(e.target.value);setSearchOpen(true)}} placeholder={mode === "seller" ? "搜我的商品或订单" : "搜索商品或卖家"} aria-label="搜索市集"/>
        {query ? <button type="button" aria-label="清空搜索" onClick={()=>{setQuery('');setActiveQuery('');setSearchOpen(true)}}><X size={14}/></button>:null}
        <button type="submit">搜索</button>
      </form><ImageProductSearch products={products} onResults={ids=>{setSearchOpen(false);setImageIds(ids);if(ids){setQuery('');setActiveQuery('');setListingFilter('all');setDiscovery('hot')}}}/></div>:null}
      {panel === "home" && searchOpen ? <section className="market-search-suggestions" aria-label="搜索建议"><header><span>{query.trim() ? "搜索建议" : "热门搜索"}</span><button type="button" aria-label="关闭搜索建议" onClick={()=>setSearchOpen(false)}><X size={16}/></button></header>{dedupeTerms([...products.map(p=>p.title), ...artists.map(a=>a.stageName), ...allSuggestions]).filter(term=>!query.trim()||term.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())).slice(0,7).map(term=><div className="market-search-suggestion" key={term}><button type="button" onClick={()=>chooseSearch(term)}><Search size={19}/><span>{term}</span></button><button type="button" aria-label={`填入搜索：${term}`} onClick={()=>{setQuery(term);setSearchOpen(true)}}><ArrowUpLeft size={22}/></button></div>)}{query.trim() && !dedupeTerms([...products.map(p=>p.title),...artists.map(a=>a.stageName),...allSuggestions]).some(term=>term.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()))?<p>暂无匹配建议，点击搜索查找商品。</p>:null}</section>:null}
      {notice ? <div className={`market-v2__notice market-v2__notice--${notice.tone}`} role="status"><CircleDollarSign size={16} /><span>{notice.text}</span><button type="button" aria-label="关闭提示" onClick={() => setNotice(null)}><X size={15} /></button></div> : null}
      <main hidden={panel === "home" && searchOpen}>{renderPanel()}</main>
      <footer hidden={panel === "home" && searchOpen} className="market-v2__trust"><ShieldCheck size={14} /><span>交易前请确认商品信息，并保留订单记录</span></footer>
    </div>
  );
}

export default MarketV2;
